import type { DealSummary } from "./api";
import { useProcedureTerms } from "../requests/terms";

/**
 * Article 12: a notifiable concentration is notified a number of days before its completion, defined once on the server (DealWatchPeriods) and
 * read through the procedure terms; undefined until they have loaded (condition 2.14).
 */
export function useNoticeDays(): number | undefined {
  return useProcedureTerms().data?.dealWatchNoticeDays;
}

export type DealSort = "score" | "value" | "announced" | "expectedClose" | "completed";

export function daysUntil(iso: string | null | undefined, today = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return null;
  const start = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const end = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((end - start) / 86_400_000);
}

export function isClosingSoon(d: Pick<DealSummary, "expectedCloseOn" | "status" | "completedOn">, noticeDays: number | undefined): boolean {
  if (noticeDays == null) return false;
  if (d.completedOn || d.status === "Completed" || d.status === "Lapsed") return false;
  const days = daysUntil(d.expectedCloseOn);
  return days != null && days <= noticeDays;
}

export function nextDealId(ids: string[], current: string | null, delta: number): string | null {
  if (!current || ids.length === 0) return current;
  const i = ids.indexOf(current);
  if (i < 0) return ids[0] ?? null;
  const n = i + delta;
  if (n < 0 || n >= ids.length) return current;
  return ids[n] ?? current;
}

export interface ProviderFacts {
  techniques: string[];
  dealType?: string;
  currency?: string;
  domestic?: boolean;
  unsolicited?: boolean;
  comment?: string;
  latestActivity?: string;
  headline?: string;
}

/** Pulls the fields the licensed Mergermarket product actually sends, without dumping the whole JSON. */
export function providerFacts(raw: unknown): ProviderFacts {
  if (!raw || typeof raw !== "object") return { techniques: [] };
  const r = raw as Record<string, unknown>;
  const techniques = Array.isArray(r.technique)
    ? r.technique.flatMap((t) => {
        if (typeof t === "string") return [t];
        if (t && typeof t === "object" && "name" in t && typeof (t as { name: unknown }).name === "string") return [(t as { name: string }).name];
        return [];
      })
    : [];
  const types = Array.isArray(r.types) ? r.types.filter((x): x is string => typeof x === "string") : [];
  const dealType = str(r.dealTypeName) ?? str(r.dealType) ?? types[0];
  return {
    techniques: unique([...techniques, ...types]),
    dealType,
    currency: str(r.dealCurrency) ?? str(r.currency),
    domestic: bool(r.isDomestic),
    unsolicited: bool(r.isUnsolicited),
    comment: str(r.comment) ?? str(r.description),
    latestActivity: str(r.latestActivityDate) ?? str(r.lastEditDate) ?? str(r.lastUpdated),
    headline: str(r.headline) ?? str(r.dealName) ?? str(r.title),
  };
}

function str(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v : undefined;
}

function bool(v: unknown): boolean | undefined {
  return typeof v === "boolean" ? v : undefined;
}

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const key = v.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}
